import * as React from 'react';
import { LogoEditorTextSettings }  from './logo-editor-text-settings.component';
import { LogoEditorLineSettings }  from './logo-editor-line-settings.component';
import { LogoEditorRectangleSettings }  from './logo-editor-rectangle-settings.component';
import { ITronEditor, ShapeType, IText, ILine, IRect, ILogo, LogoColorVariant } from '../tron-editor-api';
import { LogoEditorLogoSettings } from './logo-editor-logo-settings.component';
import { DebuggerInstance } from 'src/App/shared/debugger';
import { LogoVariantDic } from '../logo-editor-container.component';

export interface ILogoEditorObjectSettingsProps {
    editor: ITronEditor;
    selectedObjectId: number | undefined;
    logoVariantsCache: LogoVariantDic;
    initialLogoColors: string[];
}

export enum AlignOptions {
    left = 'left',
    center = 'center',
    right = 'right'
}

const maxRecentColors = 8;
const transparentColor = 'transparent';
const transparentColorRgba = 'rgba(0,0,0,0)';
const defaultCustomColor = 'rgb(255,0,0)';

interface IState {
    recentColors: string[];
    lastCustomColor: string;
}

export class LogoEditorObjectSettings extends React.Component<ILogoEditorObjectSettingsProps, IState> {
    constructor(props: ILogoEditorObjectSettingsProps) {
        super(props);

        let lastCustomColor = defaultCustomColor;
        const index = this.props.selectedObjectId;
        if (index !== undefined) {
            const visualObj = this.props.editor.visualObjects[index];
            if (visualObj.ShapeType === ShapeType.Logo) {
                const customColor = (visualObj as ILogo).customColor;
                if (customColor) {
                    lastCustomColor = customColor;
                }
            }
        }

        this.state = {
            recentColors: [transparentColor],
            lastCustomColor
        };
    }

    private addRecentColor(color: string) {
        const colors = [transparentColor];
        if (color !== transparentColor) {
            colors.push(color);
        }

        let i = 1;
        while (i < this.state.recentColors.length && colors.length < maxRecentColors)
        {
            const c = this.state.recentColors[i];
            if (c !== color && c !== transparentColor) {
                colors.push(c);
            }

            i++;
        }

        this.setState({
            recentColors: colors
        });
    }

    private onColorsChanged() {
        this.setState({});
    }

    private getSettingEditor(): JSX.Element {
        if (this.props.selectedObjectId === undefined
            || this.props.selectedObjectId === null) {
            return <div></div>;
        }

        const index: number = this.props.selectedObjectId;
        const visualObj = this.props.editor.visualObjects[index];
        if (visualObj === undefined) {
            return <div></div>;
        }
        
        if (visualObj.ShapeType === ShapeType.Text) {
            const textObj = visualObj as IText;
            return this.getTextSetting(index, textObj);
        }

        if (visualObj.ShapeType === ShapeType.Line) {
            return (<LogoEditorLineSettings
                lineObj={visualObj as ILine}
                recentColors={this.state.recentColors}
                logoColors={this.props.initialLogoColors}
                onSetColor={(type: string, color: string) => {
                    if (color === transparentColor) {
                        color = transparentColorRgba;
                    }
                    this.props.editor.setShapeProps(
                        this.props.selectedObjectId as number,
                        { StrokeColor: color });
                    this.onColorsChanged();
                }}
                onStrokeWidthChange={(width) =>{
                    this.props.editor.setShapeProps(
                        index,
                        { StrokeWidth: width });
                }}
                addPresetColor={(color) => {
                    this.addRecentColor(color)
                }}
            />)
        }

        if (visualObj.ShapeType === ShapeType.Rect) {
            return (<LogoEditorRectangleSettings
                rectangleObj={visualObj as IRect}
                recentColors={this.state.recentColors}
                logoColors={this.props.initialLogoColors}
                onSetColor={(type: string, color: string) => {
                    if (color === transparentColor) {
                        color = transparentColorRgba;
                    }
                    
                    if (type === 'fill') {
                        this.props.editor.setShapeProps(
                            this.props.selectedObjectId as number,
                            { FillColor: color });
                            this.onColorsChanged();
                    }

                    if (type === 'stroke') {
                        this.props.editor.setShapeProps(
                            this.props.selectedObjectId as number,
                            { StrokeColor: color });
                            this.onColorsChanged();
                    }
                }}
                onStrokeWidthChange={(width) =>{
                    this.props.editor.setShapeProps(
                        index,
                        { StrokeWidth: width });
                    this.setState({});
                }}
                addPresetColor={(color) => {
                    this.addRecentColor(color)
                }}
            />)
        }

        if (visualObj.ShapeType === ShapeType.Logo) {
            let customColor = (visualObj as ILogo).customColor;
            if (!customColor) {
                customColor = this.state.lastCustomColor
            }

            return (<LogoEditorLogoSettings
                logoObj={visualObj as ILogo}
                variantsInfo={this.props.logoVariantsCache[(visualObj as ILogo).LogoId]}
                customColor={customColor}
                recentColors={this.state.recentColors}
                logoColors={this.props.initialLogoColors}
                onSetLogoVariant={(nextVariant, color) => {
                    DebuggerInstance.log('setting variant');
                    DebuggerInstance.log(index);
                    DebuggerInstance.log(visualObj);
                    if (nextVariant === LogoColorVariant.Custom) {
                        let logoColor = color;
                        if (!logoColor) {
                            logoColor = this.state.lastCustomColor;
                        }
                        this.props.editor.setShapeProps(
                            index,
                            { 
                                variant: nextVariant,
                                customColor: logoColor
                            });
                        this.setState({
                            lastCustomColor: logoColor
                        });
                        this.onColorsChanged();
                    } else {
                        this.props.editor.setShapeProps(
                            index,
                            { 
                                variant: nextVariant,
                            });
                        this.onColorsChanged();
                    }
                    
                }}
                addPresetColor={(color) => {
                    this.addRecentColor(color)
                }}
            />)
        }
        
        return <div></div>;
    }

    private getTextSetting(index: number, textObj: IText): JSX.Element {
        return (
            <LogoEditorTextSettings
                textObject={textObj}
                recentColors={this.state.recentColors}
                logoColors={this.props.initialLogoColors}
                onTextChange={(text: string) => {
                    this.props.editor.setShapeProps(
                    index, 
                    { Text: text }
                )}}
                onFontChange={(font: string) => this.props.editor.setShapeProps(
                    index,
                    { FontFamily: font }
                )}
                onTextAlignLeft={() => {this.props.editor.setShapeProps(
                    index,
                    { Align: AlignOptions.left });
                    this.setState({});
                }}
                onTextAlignCenter={() => {this.props.editor.setShapeProps(
                    index,
                    { Align: AlignOptions.center });
                    this.setState({});
                }}
                onTextAlignRight={() => { this.props.editor.setShapeProps(
                    index,
                    { Align: AlignOptions.right });
                this.setState({});
                }}
                onBoldToggle={() => {
                    this.props.editor.setShapeProps(
                        index,
                        { Bold: !textObj.Bold });
                    this.setState({});
                }}
                onItalicToggle={() => {
                    this.props.editor.setShapeProps(
                        index,
                        { Italic: !textObj.Italic })
                    this.setState({});
                }}
                onSetColor={(type: string, color: string) => {
                    if (type === 'fill') {
                        if (color === transparentColor) {
                            color = transparentColorRgba;
                        }
                        this.props.editor.setShapeProps(
                            index,
                            { FillColor: color }
                        )
                    }
                    this.onColorsChanged();
                }}
                addPresetColor={(color) => {
                    this.addRecentColor(color)
                }}
            />
        )
    }

    public render() {
        return (
            <div className="editor-shape-properties-root">
                {this.getSettingEditor()}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/App/logo-app/logo-editor/logo-editor-controls/logo-editor-object-settings.component.tsx